import React, { useContext, createContext, useState, useEffect } from 'react';
import { fetchUser } from '../server-api/api/user';
import { googleAuth, login as apiLogin, logoutUser, signup as apiSignup, testerAuth } from '../server-api/api/auth';
import { User } from '../server-api/models';
import { AppContainerProps } from '../AppContainer';
import { configureUserApi } from '../server-api/api/base';

export function useAuthContext(): AuthContextType {
    return useContext(AuthContext)
}

export enum LoginType {
    tester, google, password
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    setUser: (user: any) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    loading: boolean;
    refresh: () => Promise<User | null>;
    login: (type: LoginType, idTokenOrEmail?: string, password?: string) => Promise<User | null>;
    signup: (name: string, email: string, password: string) => Promise<User | null>;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    user: null,
    setUser: () => { },
    setIsAuthenticated: () => { },
    loading: true,
    refresh: async () => null,
    login: async () => null,
    signup: async () => null,
    logout: () => { },
});

const AuthStateProvider: React.FC<AppContainerProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    async function refresh(): Promise<User | null> {
        try {
            const u = await fetchUser();
            configureUserApi(u);
            setUser(u);
            setIsAuthenticated(true);
            return u;
        } catch {
            setUser(null);
            setIsAuthenticated(false);
            return null;
        }
    }

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, []);

    function applyUser(u: User) {
        configureUserApi(u);
        setUser(u);
        setIsAuthenticated(true);
    }

    const login = async (type: LoginType, idTokenOrEmail?: string, password?: string): Promise<User | null> => {
        try {
            if (type === LoginType.tester) {
                const u = await testerAuth();
                applyUser(u);
                return u;
            }
            if (type === LoginType.google && idTokenOrEmail) {
                const u = await googleAuth(idTokenOrEmail);
                applyUser(u);
                return u;
            }
            if (type === LoginType.password && idTokenOrEmail && password !== undefined) {
                const u = await apiLogin(idTokenOrEmail, password);
                applyUser(u);
                return u;
            }
            return null;
        } catch (err) {
            setUser(null);
            setIsAuthenticated(false);
            throw err;
        }
    };

    const signup = async (name: string, email: string, password: string): Promise<User | null> => {
        try {
            const u = await apiSignup(name, email, password);
            applyUser(u);
            return u;
        } catch (err) {
            setUser(null);
            setIsAuthenticated(false);
            throw err;
        }
    };

    const logout = () => {
        logoutUser()
            .then(() => {
                setIsAuthenticated(false);
                setUser(null);
            })
            .finally(() => {
                window.location.reload();
            });
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, setUser, setIsAuthenticated, loading, refresh, login, signup, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthStateProvider;
